import React, { useState, useEffect, useRef } from "react";
import {
  CheckCircle2,
  ShieldCheck,
  Users,
  ClipboardList,
  Home,
  FileText,
  Zap,
  ArrowRight,
  BadgeIndianRupee,
  Star,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { motion, useInView } from "framer-motion";

/* ─── Advantage Cards Data ─────────────────────────────────── */
const advantages = [
  {
    icon: BadgeIndianRupee,
    title: "Direct-to-Owner Rent",
    desc: "Tenants pay you directly. We never act as a middleman for your money, ensuring zero delays and 100% transparency.",
    accent: "bg-emerald-50 text-emerald-600 border-emerald-100",
    iconBg: "bg-emerald-500",
  },
  {
    icon: ShieldCheck,
    title: "Zero Commission",
    desc: "No hidden charges, no admin fees, and no percentage-based deductions on your rental income — ever.",
    accent: "bg-blue-50 text-blue-600 border-blue-100",
    iconBg: "bg-blue-500",
  },
  {
    icon: ClipboardList,
    title: "Physical Executive Inspections",
    desc: "Our team personally visits your property to inspect for interior damages, water leakage, seepage, and general maintenance.",
    accent: "bg-amber-50 text-amber-600 border-amber-100",
    iconBg: "bg-amber-500",
  },
  {
    icon: Zap,
    title: "Minimal Vacancy Guarantee",
    desc: "If a tenant vacates, we provide a replacement tenant within days, ensuring your rental income stays consistent.",
    accent: "bg-red-50 text-red-600 border-red-100",
    iconBg: "bg-red-500",
  },
  {
    icon: FileText,
    title: "Rental Agreement",
    desc: "Rental agreements drafted by the HousyHunt team with all terms mutually agreed upon between tenant and owner.",
    accent: "bg-purple-50 text-purple-600 border-purple-100",
    iconBg: "bg-purple-500",
  },
];

/* ─── Stats ─────────────────────────────────────────────────── */
const stats = [
  { value: "₹0", label: "Commission Charged" },
  { value: "100%", label: "Rent Transparency" },
  { value: "3 Days", label: "Avg. Tenant Replacement" },
  { value: "24/7", label: "Owner Support" },
];

/* ─── Fade-in wrapper ────────────────────────────────────────── */
const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Main Component ─────────────────────────────────────────── */
const ExpertManagementPlan = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section className="w-full bg-gray-50 min-h-screen">

      {/* ══════════════════════════════════════════
          HERO BANNER
      ══════════════════════════════════════════ */}
      <div className="relative w-full overflow-hidden bg-white border-b border-gray-100">

        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=80&fit=crop"
            alt="Property background"
            className="w-full h-full object-cover object-center opacity-10"
          />
          {/* gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-24 flex flex-col lg:flex-row items-center gap-12 lg:gap-0">

          {/* LEFT — text */}
          <div className="flex-1">
            {/* pill tag */}
            <FadeIn delay={0}>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide mb-6">
                <Star size={12} fill="currentColor" />
                HOUSYHUNT EXPERT MANAGEMENT PLAN
              </div>
            </FadeIn>

            <FadeIn delay={0.06}>
              <h1 className="text-3xl sm:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight mb-4">
                Professional Property Management.
                <br />
                <span className="text-red-600">Zero Commission.</span>
                <br />
                <span className="text-red-600">Zero Vacancy.</span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.12}>
              <p className="text-gray-500 text-base sm:text-lg leading-relaxed max-w-xl mb-8">
                Traditional agencies tax your investment success with percentage-based fees.
                HousyHunt offers a flat-fee annual subscription that prioritizes your profit
                and property health — nothing more, nothing less.
              </p>
            </FadeIn>

            {/* CTA row */}
            <FadeIn delay={0.18}>
              <div className="flex flex-wrap items-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="group flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-3.5 rounded-full font-semibold text-sm shadow-lg shadow-red-600/25 transition-all duration-300"
                >
                  Get Started Today
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
            

<a
  href="https://wa.me/message/MDG2ZSZNG6P6G1"
  target="_blank"
  rel="noopener noreferrer"
  className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-6 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
>
  <FaWhatsapp className="text-2xl" />
  <span>Talk to an Expert</span>
</a>
              </div>
            </FadeIn>
          </div>

          {/* RIGHT — Pricing card */}
          <FadeIn delay={0.22} className="w-full lg:w-auto lg:pl-16">
            <div className="relative">
              {/* glow ring */}
              <div className="absolute -inset-1 bg-gradient-to-br from-red-400 to-red-600 rounded-3xl blur opacity-20" />

              <div className="relative bg-white border border-gray-100 rounded-3xl shadow-2xl shadow-red-900/10 p-8 sm:p-10 min-w-[280px] sm:min-w-[320px]">

                {/* badge */}
                <span className="inline-block bg-red-600 text-white text-[11px] font-bold tracking-widest px-3 py-1 rounded-full mb-6">
                  ANNUAL PLAN
                </span>

                <div className="mb-1">
                  <span className="text-4xl sm:text-5xl font-bold text-gray-900">₹24,999</span>
                </div>
                <p className="text-gray-400 text-sm font-medium mb-7">Flat fee · Billed annually</p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Direct-to-owner rent collection",
                    "Zero commission, zero hidden fees",
                    "Physical property inspections",
                    "Tenant replacement in days",
                    "Full rental agreement support",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 size={17} className="text-red-500 mt-0.5 shrink-0" />
                      <span className="text-gray-600 text-sm leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white rounded-2xl py-3.5 font-semibold text-sm shadow-lg shadow-red-500/25 transition-all duration-300"
                >
                  Subscribe Now
                </motion.button>

                <p className="text-center text-gray-400 text-xs mt-4">
                  No lock-in. Cancel anytime.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          STATS STRIP
      ══════════════════════════════════════════ */}
      <div className="w-full bg-gradient-to-r from-red-700 via-red-600 to-red-500">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 sm:grid-cols-4 gap-px">
          {stats.map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.07}>
              <div className="flex flex-col items-center justify-center py-4 px-2 text-center">
                <span className="text-2xl sm:text-3xl font-bold text-white mb-1">{s.value}</span>
                <span className="text-red-200 text-xs sm:text-sm font-medium">{s.label}</span>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          THE HOUSYHUNT ADVANTAGE
      ══════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-24">

        {/* Section header */}
        <FadeIn className="text-center mb-14">
          <p className="text-red-600 text-xs font-bold tracking-widest uppercase mb-3">
            Why Choose HousyHunt
          </p>
          <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-gray-900 mb-4">
            The HousyHunt Advantage
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Every feature is designed to put more money in your pocket and more peace
            in your life — with absolute transparency at every step.
          </p>
        </FadeIn>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {advantages.map((adv, i) => {
            const Icon = adv.icon;
            const isHovered = hovered === i;
            return (
              <FadeIn key={adv.title} delay={i * 0.07}>
                <motion.div
                  whileHover={{ y: -5 }}
                  onHoverStart={() => setHovered(i)}
                  onHoverEnd={() => setHovered(null)}
                  className={`relative bg-white border rounded-2xl p-7 cursor-default transition-all duration-300 h-full flex flex-col gap-5 ${
                    isHovered
                      ? "border-red-200 shadow-xl shadow-red-900/10"
                      : "border-gray-100 shadow-sm"
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${adv.iconBg} transition-all duration-300`}
                  >
                    <Icon size={22} className="text-white" />
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-2">{adv.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{adv.desc}</p>
                  </div>

                  {/* bottom accent line */}
                  <div
                    className={`absolute bottom-0 left-6 right-6 h-0.5 rounded-full transition-all duration-500 ${
                      isHovered ? "bg-red-500 opacity-100" : "bg-transparent opacity-0"
                    }`}
                  />
                </motion.div>
              </FadeIn>
            );
          })}

          {/* Wide "vs Traditional" card */}
          <FadeIn delay={0.35} className="sm:col-span-2 lg:col-span-3">
            <div className="relative bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">

                {/* LEFT — image */}
                <div className="relative min-h-[220px] md:min-h-0">
                  <img
                    src="https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&q=80&fit=crop"
                    alt="Property inspection"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20 md:bg-gradient-to-r md:from-transparent md:to-white/10" />
                </div>

                {/* RIGHT — comparison */}
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  <p className="text-red-600 text-xs font-bold tracking-widest uppercase mb-3">
                    The Real Difference
                  </p>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
                    HousyHunt vs Traditional Agencies
                  </h3>

                  <div className="space-y-4">
                    {[
                      {
                        label: "Commission",
                        them: "1–2 months rent/year",
                        us: "₹24,999 flat",
                      },
                      {
                        label: "Rent collection",
                        them: "Via agency account",
                        us: "Direct to owner",
                      },
                      {
                        label: "Vacancy handling",
                        them: "No guarantee",
                        us: "Replacement in days",
                      },
                      {
                        label: "Transparency",
                        them: "Limited visibility",
                        us: "100% visibility",
                      },
                    ].map((row) => (
                      <div
                        key={row.label}
                        className="grid grid-cols-3 gap-2 text-sm border-b border-gray-50 pb-3 last:border-0 last:pb-0"
                      >
                        <span className="text-gray-400 font-medium">{row.label}</span>
                        <span className="text-gray-400 line-through text-center">{row.them}</span>
                        <span className="text-red-600 font-semibold text-right">{row.us}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          TRUST / TESTIMONIAL STRIP
      ══════════════════════════════════════════ */}
      <div className="bg-white border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">

          <FadeIn>
            <div className="flex flex-col gap-4">
              <p className="text-red-600 text-xs font-bold tracking-widest uppercase">What Owners Say</p>
              <blockquote className="text-xl sm:text-2xl font-semibold text-gray-900 leading-snug">
                "Switched from a traditional agency and saved over ₹40,000 in the
                first year alone. HousyHunt does exactly what they promise."
              </blockquote>
              <div className="flex items-center gap-3 mt-2">
                <img
                  src="https://randomuser.me/api/portraits/men/32.jpg"
                  alt="Owner"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Rajan Mehta</p>
                  <p className="text-xs text-gray-400">Property Owner · Bengaluru</p>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={13} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=80&fit=crop"
                alt="Managed property"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="text-white text-sm font-semibold">Managed by HousyHunt</span>
                <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  Zero Vacancy
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          BOTTOM CTA
      ══════════════════════════════════════════ */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1600&q=80&fit=crop"
            alt="Cityscape"
            className="w-full h-full object-cover object-bottom opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-gray-50/95 to-gray-50" />
        </div>

        <FadeIn className="relative z-10 max-w-3xl mx-auto text-center px-6 py-20 sm:py-28">
          <p className="text-red-600 text-xs font-bold tracking-widest uppercase mb-4">
            Start Today
          </p>
          <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-gray-900 mb-5">
            Ready to experience property management
            that actually works for you?
          </h2>
          <p className="text-gray-500 mb-9 text-base leading-relaxed">
            Join hundreds of property owners across Bengaluru who trust HousyHunt
            to protect their investment and maximize their returns — at a flat fee.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="group flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-semibold text-sm shadow-xl shadow-red-600/30 transition-all duration-300"
            >
              Subscribe for ₹24,999 / Year
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <button className="border border-gray-200 hover:border-red-200 text-gray-700 hover:text-red-600 hover:bg-red-50 px-7 py-4 rounded-full font-semibold text-sm transition-all duration-300 bg-white">
              Schedule a Free Consultation
            </button>
          </div>
        </FadeIn>
      </div>

    </section>
  );
};

export default ExpertManagementPlan;